import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Trophy, TrendingUp, TrendingDown, Square, CheckSquare, ArrowDownWideNarrow, Diff, Camera, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { computeQuality, qualityBand, qualityColorClasses } from '@/lib/qualityScore';
import InlinePopover from '@/components/InlinePopover';
import ShareToXButton from '@/components/ShareToXButton';

/**
 * CompareQualityModal
 * --------------------
 * Side-by-side Quality breakdown of every ticker in the user's
 * watchlist. Dividend / Non-Dividend / Cross-Type modes.
 *
 * Round 105e (Jun 2026) — 7-point upgrade:
 *   1. Missing data renders a muted "—" (click for why) instead of a
 *      misleading 0.
 *   2. Pillar labels are click-popovers explaining what feeds each
 *      score (phone-friendly).
 *   3. "Why did X win?" expandable one-liner under the Winners bar.
 *   4. Sort-by-score toggle puts the winner column first.
 *   5. Δ vs winner mode re-renders cells as the gap to the row leader.
 *   6. Cross-Type composite row shows PERCENTILE RANKS within each
 *      ticker's own cohort (D vs ND rubrics aren't comparable raw).
 *   7. Share to X + copy-as-image buttons in the footer.
 */

const PILLARS = [
  { key: 'safety',       label: 'Safety',        max: 30,  better: 'high',
    tip: 'Safety /30 — payout ratio + free-cash-flow dividend coverage. Can the company comfortably afford its dividend?' },
  { key: 'yield',        label: 'Yield',         max: 25,  better: 'high',
    tip: 'Yield /25 — current dividend yield scored against sustainable ranges. Very high yields earn fewer points than healthy mid-range ones.' },
  { key: 'growth',       label: 'Growth (5y)',   max: 20,  better: 'high',
    tip: 'Growth (5y) /20 — five-year dividend growth rate (CAGR). Rewards consistent raisers.' },
  { key: 'longevity',    label: 'Longevity',     max: 25,  better: 'high',
    tip: 'Longevity /25 — consecutive years of dividend increases (streak). Kings and Aristocrats max this out.' },
  { key: 'fundamentals', label: 'Fundamentals',  max: 13,  better: 'high',
    tip: 'Fundamentals /13 — bonus points from revenue growth, margins and balance-sheet strength.' },
  { key: 'penalty',      label: 'Penalty',       max: 20,  better: 'low',
    tip: 'Penalty /20 — deductions for recent cuts, stretched payout, or weak coverage. LOWER is better.' },
];

const PILLARS_NONDIV = [
  { key: 'rev_cagr', label: 'Revenue 3y CAGR', max: 20, better: 'high',
    tip: 'Revenue 3y CAGR /20 — three-year compound revenue growth.' },
  { key: 'eps_cagr', label: 'EPS 3y CAGR',     max: 20, better: 'high',
    tip: 'EPS 3y CAGR /20 — three-year compound earnings-per-share growth.' },
  { key: 'roe',      label: 'ROE',             max: 15, better: 'high',
    tip: 'ROE /15 — return on equity; how efficiently shareholder capital turns into profit.' },
  { key: 'fcf',      label: 'Free Cash Flow',  max: 15, better: 'high',
    tip: 'Free Cash Flow /15 — positive, growing free cash flow earns full points.' },
  { key: 'gm',       label: 'Gross Margin',    max: 10, better: 'high',
    tip: 'Gross Margin /10 — pricing power; higher margins score better.' },
  { key: 'de',       label: 'Debt / Equity',   max: 10, better: 'high',
    tip: 'Debt / Equity /10 — balance-sheet leverage; LOWER debt earns MORE points.' },
  { key: 'pe',       label: 'P/E Valuation',   max: 10, better: 'high',
    tip: 'P/E Valuation /10 — earnings-multiple sanity check; reasonable valuations score best.' },
];

const PILLARS_CROSS = [
  { key: 'revenue_growth',    label: 'Revenue Growth (1y)', better: 'high', format: 'pct',
    tip: 'Revenue Growth (1y) — raw year-over-year revenue growth, directly comparable across dividend and growth names.' },
  { key: 'return_on_equity',  label: 'ROE',                 better: 'high', format: 'pct',
    tip: 'ROE — raw return on equity from the latest financials.' },
  { key: 'free_cash_flow',    label: 'Free Cash Flow',      better: 'high', format: 'money',
    tip: 'Free Cash Flow — raw trailing FCF in dollars. Positive = the business funds itself.' },
  { key: 'gross_margin',      label: 'Gross Margin',        better: 'high', format: 'pct',
    tip: 'Gross Margin — raw gross profit ÷ revenue. Higher = more pricing power.' },
  { key: 'debt_to_equity',    label: 'Debt / Equity',       better: 'low',  format: 'ratio',
    tip: 'Debt / Equity — raw leverage ratio. LOWER is better.' },
];

function getCrossTypeRaw(stock, key) {
  if (!stock) return null;
  const v = stock[key];
  return Number.isFinite(Number(v)) ? Number(v) : null;
}

function fmtCrossValue(v, format, { signed = false } = {}) {
  if (v == null) return '—';
  const sign = signed && v > 0 ? '+' : '';
  if (format === 'pct') return `${sign}${(v * 100).toFixed(1)}%`;
  if (format === 'money') {
    const abs = Math.abs(v);
    const neg = v < 0 ? '-' : sign;
    if (abs >= 1e9) return `${neg}$${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${neg}$${(abs / 1e6).toFixed(0)}M`;
    if (abs >= 1e3) return `${neg}$${(abs / 1e3).toFixed(0)}K`;
    return `${neg}$${abs.toFixed(0)}`;
  }
  return `${sign}${v.toFixed(2)}`;
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

const MAX_SELECTED = 6;

function getPillarValue(quality, key, mode = 'dividend', stock = null) {
  if (mode === 'cross_type') {
    return getCrossTypeRaw(stock, key);
  }
  if (!quality) return null;
  if (mode === 'non_dividend') {
    const pts = quality.non_div_points;
    if (!pts) return null;
    const v = pts[key];
    return v == null ? null : v;
  }
  if (key === 'penalty') return quality.penalty || 0;
  // Round 105e — missing pillar data is NULL (renders "—"), not a
  // silent 0, so users can tell "scored zero" from "couldn't score".
  return quality.breakdown?.[key] ?? null;
}

function findLeader(rows, key, pillarSet, mode) {
  const valid = rows
    .map(r => ({ ticker: r.ticker, v: getPillarValue(r.quality, key, mode, r.stock) }))
    .filter(r => r.v != null);
  if (valid.length === 0) return null;
  const better = pillarSet.find(p => p.key === key)?.better || 'high';
  const sorted = better === 'high'
    ? valid.sort((a, b) => b.v - a.v)
    : valid.sort((a, b) => a.v - b.v);
  if (sorted.length === 1 || sorted[0].v !== sorted[1].v) {
    return sorted[0];
  }
  return null;
}

function findTotalLeader(rows) {
  const valid = rows.filter(r => r.quality?.score != null);
  if (valid.length === 0) return null;
  const sorted = [...valid].sort((a, b) => b.quality.score - a.quality.score);
  if (sorted.length === 1 || sorted[0].quality.score !== sorted[1].quality.score) {
    return { ticker: sorted[0].ticker, v: sorted[0].quality.score };
  }
  return null;
}

function pillarToneCls(pct, isNegative) {
  if (isNegative) return 'bg-rose-500';
  if (pct >= 0.85) return 'bg-emerald-500';
  if (pct >= 0.6)  return 'bg-green-500';
  if (pct >= 0.4)  return 'bg-amber-500';
  if (pct > 0)     return 'bg-orange-500';
  return 'bg-muted-foreground/30';
}

export default function CompareQualityModal({ open, onClose, watchlistStocks = [], onStockClick }) {
  const [mode, setMode] = useState('dividend');
  // Round 105e — new view controls.
  const [sortByScore, setSortByScore] = useState(false);
  const [deltaMode, setDeltaMode] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const gridRef = useRef(null);

  const visibleStocks = useMemo(() => {
    if (mode === 'non_dividend') {
      return watchlistStocks.filter(s => s?.pays_dividend === false);
    }
    if (mode === 'cross_type') {
      return watchlistStocks;
    }
    return watchlistStocks.filter(s => s?.pays_dividend !== false);
  }, [watchlistStocks, mode]);

  const pillarSet = mode === 'non_dividend' ? PILLARS_NONDIV
    : mode === 'cross_type' ? PILLARS_CROSS
    : PILLARS;

  const initialTickers = useMemo(
    () => visibleStocks.slice(0, MAX_SELECTED).map(s => s.ticker),
    [visibleStocks]
  );
  const [selected, setSelected] = useState(initialTickers);

  useEffect(() => {
    if (open) {
      setSelected(visibleStocks.slice(0, MAX_SELECTED).map(s => s.ticker));
      setWhyOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, visibleStocks.length]);

  const validSelected = useMemo(
    () => selected.filter(t => visibleStocks.some(s => s.ticker === t)),
    [selected, visibleStocks]
  );

  const rows = useMemo(
    () => validSelected.map(t => {
      const stock = visibleStocks.find(s => s.ticker === t);
      return { ticker: t, stock, quality: computeQuality(stock) };
    }),
    [validSelected, visibleStocks]
  );

  // Round 105e (#4) — optional composite-sorted column order so the
  // winner sits leftmost where the eye starts.
  const displayRows = useMemo(() => {
    if (!sortByScore) return rows;
    return [...rows].sort((a, b) => (b.quality?.score ?? -1) - (a.quality?.score ?? -1));
  }, [rows, sortByScore]);

  const leaders = useMemo(() => {
    const out = {};
    for (const p of pillarSet) {
      out[p.key] = findLeader(rows, p.key, pillarSet, mode);
    }
    out.total = findTotalLeader(rows);
    return out;
  }, [rows, pillarSet, mode]);

  // Round 105e (#6) — Cross-Type percentile ranks. D and ND rubrics
  // aren't comparable raw, so the composite row normalizes each
  // ticker's score to a percentile WITHIN ITS OWN COHORT across the
  // entire watchlist (bigger sample than just the selection).
  const cohortPercentiles = useMemo(() => {
    if (mode !== 'cross_type') return {};
    const cohorts = { D: [], ND: [] };
    for (const s of watchlistStocks) {
      const q = computeQuality(s);
      if (q?.score != null) {
        cohorts[s?.pays_dividend === false ? 'ND' : 'D'].push(q.score);
      }
    }
    const out = {};
    for (const r of rows) {
      const score = r.quality?.score;
      const cohortKey = r.stock?.pays_dividend === false ? 'ND' : 'D';
      const cohort = cohorts[cohortKey];
      if (score == null || !cohort.length) { out[r.ticker] = null; continue; }
      const below = cohort.filter(x => x <= score).length;
      out[r.ticker] = { pct: Math.round((below / cohort.length) * 100), cohort: cohortKey, n: cohort.length };
    }
    return out;
  }, [mode, watchlistStocks, rows]);

  // Cross-Type "overall" = highest in-cohort percentile (comparable),
  // not the raw composite (not comparable across rubrics).
  const crossPctLeader = useMemo(() => {
    if (mode !== 'cross_type') return null;
    const entries = Object.entries(cohortPercentiles).filter(([, v]) => v != null);
    if (!entries.length) return null;
    const sorted = entries.sort((a, b) => b[1].pct - a[1].pct);
    if (sorted.length === 1 || sorted[0][1].pct !== sorted[1][1].pct) {
      return { ticker: sorted[0][0], pct: sorted[0][1].pct };
    }
    return null;
  }, [mode, cohortPercentiles]);

  const winnerSummary = useMemo(() => {
    const divLabelMap = {
      safety: 'Safety', yield: 'Yield', growth: 'Growth',
      longevity: 'Longevity', fundamentals: 'Fundamentals',
    };
    const ndLabelMap = {
      rev_cagr: 'Revenue', eps_cagr: 'EPS', roe: 'ROE',
      fcf: 'Free Cash Flow', gm: 'Gross Margin',
      de: 'Debt/Equity', pe: 'P/E',
    };
    const crossLabelMap = {
      revenue_growth: 'Revenue Growth', return_on_equity: 'ROE',
      free_cash_flow: 'FCF', gross_margin: 'Gross Margin',
      debt_to_equity: 'Debt/Equity',
    };
    const labelMap = mode === 'non_dividend' ? ndLabelMap
      : mode === 'cross_type' ? crossLabelMap
      : divLabelMap;
    const keys = Object.keys(labelMap);
    const parts = [];
    for (const k of keys) {
      const w = leaders[k];
      if (w) parts.push(`${w.ticker} wins ${labelMap[k]}`);
    }
    return parts;
  }, [leaders, mode]);

  // Round 105e (#3) — plain-English "why did X win?" line.
  const whyText = useMemo(() => {
    if (mode === 'cross_type') {
      if (!crossPctLeader) return null;
      const won = pillarSet.filter(p => leaders[p.key]?.ticker === crossPctLeader.ticker).map(p => p.label);
      let s = `${crossPctLeader.ticker} ranks highest within its own cohort (${ordinal(crossPctLeader.pct)} percentile)`;
      s += won.length ? ` and wins ${won.join(', ')} on raw fundamentals.` : ', though other tickers win individual raw-metric rows.';
      return s;
    }
    const total = leaders.total;
    if (!total) return null;
    const won = pillarSet.filter(p => p.key !== 'penalty' && leaders[p.key]?.ticker === total.ticker).map(p => p.label);
    const winnerRow = rows.find(r => r.ticker === total.ticker);
    let weakest = null;
    if (winnerRow) {
      let worst = null;
      for (const p of pillarSet) {
        if (p.key === 'penalty') continue;
        const v = getPillarValue(winnerRow.quality, p.key, mode, winnerRow.stock);
        if (v == null || !p.max) continue;
        const frac = v / p.max;
        if (worst == null || frac < worst.frac) worst = { label: p.label, frac };
      }
      weakest = worst && worst.frac < 0.5 ? worst.label : null;
    }
    let s = `${total.ticker} takes the overall crown (${total.v}/100)`;
    s += won.length
      ? ` by winning ${won.join(', ')}.`
      : ' with balanced scores — no single-pillar wins, just fewer weak spots than the rest.';
    if (weakest) s += ` Its softest area is ${weakest}.`;
    return s;
  }, [leaders, rows, pillarSet, mode, crossPctLeader]);

  // Round 105e (#7) — share text + image capture.
  const shareText = useMemo(() => {
    if (!rows.length) return '';
    const scores = rows.map(r => `$${r.ticker} ${r.quality?.score ?? '—'}`).join(' · ');
    const winner = mode === 'cross_type'
      ? (crossPctLeader ? ` — ${crossPctLeader.ticker} leads its cohort` : '')
      : (leaders.total ? ` — ${leaders.total.ticker} wins overall` : '');
    return `Quality face-off on my watchlist: ${scores}${winner}`;
  }, [rows, leaders, mode, crossPctLeader]);

  const handleCopyImage = async () => {
    if (!gridRef.current || copying) return;
    setCopying(true);
    try {
      const { toPng } = await import('html-to-image');
      const bg = getComputedStyle(document.body).backgroundColor || '#09090b';
      const dataUrl = await toPng(gridRef.current, { pixelRatio: 2, backgroundColor: bg });
      // Round 105f — stamp a small brand watermark in the bottom-right
      // corner so shared screenshots drive traffic back to the site.
      const stampedUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const c = document.createElement('canvas');
            c.width = img.width;
            c.height = img.height;
            const ctx = c.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const label = 'DiviDrip · twylightcrow.com';
            const fs = Math.max(20, Math.round(img.width * 0.014));
            ctx.font = `bold ${fs}px ui-monospace, monospace`;
            const tw = ctx.measureText(label).width;
            const padX = fs * 0.6;
            const padY = fs * 0.45;
            const w = tw + padX * 2;
            const h = fs + padY * 2;
            const x = img.width - w - 14;
            const y = img.height - h - 14;
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            if (typeof ctx.roundRect === 'function') {
              ctx.beginPath();
              ctx.roundRect(x, y, w, h, 8);
              ctx.fill();
            } else {
              ctx.fillRect(x, y, w, h);
            }
            ctx.fillStyle = '#e879f9';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, x + padX, y + h / 2 + 1);
            resolve(c.toDataURL('image/png'));
          } catch (err) { reject(err); }
        };
        img.onerror = reject;
        img.src = dataUrl;
      });
      const blob = await (await fetch(stampedUrl)).blob();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        toast.success('Comparison image copied to clipboard');
      } else {
        const a = document.createElement('a');
        a.href = stampedUrl;
        a.download = 'compare-quality.png';
        a.click();
        toast.success('Comparison image downloaded');
      }
    } catch {
      toast.error("Couldn't capture the image — try again");
    } finally {
      setCopying(false);
    }
  };

  if (!open) return null;

  const toggleTicker = (ticker) => {
    setSelected(prev => {
      if (prev.includes(ticker)) {
        return prev.filter(t => t !== ticker);
      }
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, ticker];
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4"
      onClick={onClose}
      data-testid="compare-quality-overlay"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="compare-quality-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" />
            <div>
              <h2 className="text-base font-bold">Compare Quality</h2>
              <p className="text-[11px] text-muted-foreground">
                Side-by-side breakdown of your Watchlist tickers (up to {MAX_SELECTED} at a time).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md ring-1 ring-border overflow-hidden text-[10px] font-bold uppercase tracking-wider" data-testid="compare-quality-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('dividend')}
                className={`px-2.5 py-1 transition-colors ${mode === 'dividend' ? 'bg-fuchsia-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted-foreground/15'}`}
                data-testid="compare-quality-mode-dividend"
              >
                Dividend
              </button>
              <button
                type="button"
                onClick={() => setMode('non_dividend')}
                className={`px-2.5 py-1 transition-colors ${mode === 'non_dividend' ? 'bg-violet-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted-foreground/15'}`}
                data-testid="compare-quality-mode-non-dividend"
              >
                Non-Dividend
              </button>
              <button
                type="button"
                onClick={() => setMode('cross_type')}
                className={`px-2.5 py-1 transition-colors ${mode === 'cross_type' ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted-foreground/15'}`}
                data-testid="compare-quality-mode-cross-type"
                title="Compare both dividend and non-dividend tickers using OVERLAPPING fundamentals — useful for deciding whether to add a growth name to a dividend portfolio"
              >
                Cross-Type
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-muted"
              data-testid="compare-quality-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Selection chips + view controls */}
        <div className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
              Tap to toggle ({validSelected.length}/{MAX_SELECTED})
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSortByScore(v => !v)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ring-1 transition-colors ${
                  sortByScore
                    ? 'bg-fuchsia-600 text-white ring-fuchsia-500'
                    : 'bg-muted text-muted-foreground ring-border hover:bg-muted-foreground/15'
                }`}
                data-testid="compare-quality-sort-toggle"
                title="Order the columns by composite score so the winner sits first"
              >
                <ArrowDownWideNarrow className="w-3 h-3" />
                Sort by score
              </button>
              <button
                type="button"
                onClick={() => setDeltaMode(v => !v)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ring-1 transition-colors ${
                  deltaMode
                    ? 'bg-fuchsia-600 text-white ring-fuchsia-500'
                    : 'bg-muted text-muted-foreground ring-border hover:bg-muted-foreground/15'
                }`}
                data-testid="compare-quality-delta-toggle"
                title="Show each cell as the gap to that row's winner instead of the raw value"
              >
                <Diff className="w-3 h-3" />
                Δ vs winner
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`compare-quality-chips-${mode}`}>
            {visibleStocks.length === 0 && (
              <div className="text-xs text-muted-foreground italic">
                {mode === 'non_dividend'
                  ? 'No non-dividend tickers in your watchlist yet.'
                  : mode === 'cross_type'
                  ? 'Your watchlist is empty.'
                  : 'No dividend-paying tickers in your watchlist yet.'}
              </div>
            )}
            {visibleStocks.map(s => {
              const isSel = validSelected.includes(s.ticker);
              const atCap = !isSel && validSelected.length >= MAX_SELECTED;
              return (
                <button
                  key={s.ticker}
                  type="button"
                  onClick={() => toggleTicker(s.ticker)}
                  disabled={atCap}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    isSel
                      ? 'bg-fuchsia-600 text-white'
                      : 'bg-muted hover:bg-muted-foreground/20 text-foreground'
                  }`}
                  data-testid={`compare-quality-toggle-${s.ticker}`}
                  title={atCap ? `Maximum ${MAX_SELECTED} tickers selected — un-pick another first` : isSel ? 'Remove from comparison' : 'Add to comparison'}
                >
                  {isSel ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                  {s.ticker}
                </button>
              );
            })}
          </div>
        </div>

        {/* Winner summary line + "Why?" expander */}
        {validSelected.length >= 2 && winnerSummary.length > 0 && (
          <div className="px-4 py-2 border-b bg-fuchsia-50/50 dark:bg-fuchsia-950/20 shrink-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[11px] text-fuchsia-900 dark:text-fuchsia-200">
                <Trophy className="w-3 h-3 inline mr-1 -mt-0.5" />
                <strong>Winners:</strong> {winnerSummary.join(' · ')}
                {mode === 'cross_type'
                  ? (crossPctLeader && <> · <strong>{crossPctLeader.ticker}</strong> highest in-cohort percentile ({ordinal(crossPctLeader.pct)})</>)
                  : (leaders.total && <> · <strong>{leaders.total.ticker}</strong> overall ({leaders.total.v}/100)</>)}
              </p>
              {whyText && (
                <button
                  type="button"
                  onClick={() => setWhyOpen(v => !v)}
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 hover:underline underline-offset-2 shrink-0"
                  data-testid="compare-quality-why-toggle"
                >
                  Why?
                  <ChevronDown className={`w-3 h-3 transition-transform ${whyOpen ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
            {whyOpen && whyText && (
              <p className="mt-1.5 text-[11px] leading-snug text-fuchsia-900/90 dark:text-fuchsia-100/90" data-testid="compare-quality-why-text">
                {whyText}
              </p>
            )}
          </div>
        )}

        {/* Comparison grid */}
        <div className="flex-1 overflow-auto pb-24 sm:pb-4">
          {validSelected.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Pick at least one ticker above to start comparing.
            </div>
          ) : (
            <div className="overflow-x-auto" ref={gridRef}>
              <table className="w-full min-w-max border-collapse">
                <thead className="sticky top-0 bg-card z-10 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                      Pillar
                    </th>
                    {displayRows.map(r => {
                      const band = qualityBand(r.quality?.score);
                      const cls = qualityColorClasses(band.tone);
                      return (
                        <th key={r.ticker} className="px-3 py-2 text-center min-w-[110px]" data-testid={`compare-col-${r.ticker}`}>
                          <button
                            type="button"
                            onClick={() => onStockClick?.(r.ticker)}
                            className="font-mono text-sm font-bold text-yellow-600 dark:text-yellow-400 hover:underline block"
                          >
                            {r.ticker}
                          </button>
                          <span className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full px-1.5 py-0.5 text-[10px] gap-1 mt-0.5 ${cls.bg} ${cls.text} ${cls.ring}`}>
                            <span className={`rounded-full w-1.5 h-1.5 ${cls.dot}`} />
                            {r.quality?.score ?? '—'}
                            <span className="opacity-70">{band.short}</span>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pillarSet.map((p) => {
                    const leader = leaders[p.key];
                    let crossMaxAbs = null;
                    if (mode === 'cross_type') {
                      const vals = rows
                        .map(r => getPillarValue(r.quality, p.key, mode, r.stock))
                        .filter(v => v != null)
                        .map(v => Math.abs(v));
                      crossMaxAbs = vals.length ? Math.max(...vals) : null;
                    }
                    // Best value in the row for Δ mode.
                    const rowVals = rows
                      .map(r => getPillarValue(r.quality, p.key, mode, r.stock))
                      .filter(v => v != null);
                    const bestVal = rowVals.length
                      ? (p.better === 'low' ? Math.min(...rowVals) : Math.max(...rowVals))
                      : null;
                    return (
                      <tr key={p.key} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-3 py-2.5 text-xs font-semibold whitespace-nowrap">
                          <InlinePopover
                            className="inline-flex items-baseline gap-1 cursor-help border-b border-dotted border-muted-foreground/40 hover:border-muted-foreground"
                            title={p.tip}
                            testId={`compare-pillar-tip-${p.key}`}
                            maxWidth={260}
                            render={() => (
                              <p className="text-left text-[11px] leading-snug text-foreground/90">{p.tip}</p>
                            )}
                          >
                            {p.label}
                            {p.max != null && (
                              <span className="text-[10px] text-muted-foreground font-mono">
                                /{p.max}
                              </span>
                            )}
                          </InlinePopover>
                          {mode === 'cross_type' && p.better === 'low' && (
                            <span className="text-[9px] text-muted-foreground ml-1 italic">(lower = better)</span>
                          )}
                        </td>
                        {displayRows.map(r => {
                          const v = getPillarValue(r.quality, p.key, mode, r.stock);
                          const isWinner = leader && leader.ticker === r.ticker;
                          const isNegative = v != null && v < 0;
                          let pct = 0;
                          if (v != null) {
                            if (mode === 'cross_type') {
                              pct = crossMaxAbs && crossMaxAbs > 0
                                ? Math.max(0, Math.min(1, Math.abs(v) / crossMaxAbs))
                                : 0;
                            } else {
                              pct = Math.max(0, Math.min(1, Math.abs(v) / p.max));
                            }
                          }
                          // Round 105e (#1) — null renders a muted dash with
                          // a click explainer. (#5) — Δ mode shows the gap
                          // to the row's best value for non-winners.
                          if (v == null) {
                            return (
                              <td key={r.ticker} className="px-3 py-2.5 text-center">
                                <InlinePopover
                                  className="text-muted-foreground/60 font-mono text-[11px] cursor-help"
                                  title={`No data — ${p.label} couldn't be scored for ${r.ticker}`}
                                  testId={`compare-nodata-${p.key}-${r.ticker}`}
                                  maxWidth={240}
                                  render={() => (
                                    <p className="text-left text-[11px] leading-snug text-foreground/90">
                                      No data — {p.label} couldn&apos;t be scored for {r.ticker} (missing inputs). This is NOT counted as a zero.
                                    </p>
                                  )}
                                >
                                  —
                                </InlinePopover>
                              </td>
                            );
                          }
                          const showDelta = deltaMode && bestVal != null && v !== bestVal;
                          let display;
                          if (showDelta) {
                            const d = v - bestVal;
                            display = mode === 'cross_type'
                              ? fmtCrossValue(d, p.format, { signed: true })
                              : `${d > 0 ? '+' : ''}${d}`;
                          } else {
                            display = mode === 'cross_type'
                              ? fmtCrossValue(v, p.format)
                              : `${v > 0 && p.key !== 'penalty' ? '+' : ''}${v}`;
                          }
                          return (
                            <td key={r.ticker} className="px-3 py-2.5">
                              <div className="flex items-center justify-center gap-1.5">
                                {isWinner && (
                                  <Trophy className="w-3 h-3 text-amber-500 shrink-0" data-testid={`compare-winner-${p.key}-${r.ticker}`} />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center justify-between text-[10px] mb-0.5">
                                    <span className={`font-mono font-bold ${
                                      showDelta
                                        ? 'text-muted-foreground'
                                        : isNegative
                                        ? 'text-rose-600 dark:text-rose-400'
                                        : isWinner
                                        ? 'text-emerald-700 dark:text-emerald-300'
                                        : 'text-foreground'
                                    }`}>
                                      {display}
                                    </span>
                                  </div>
                                  <div className="h-1 rounded-full bg-muted/60 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${pillarToneCls(pct, isNegative)}`}
                                      style={{ width: `${pct * 100}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* Composite total — Cross-Type shows in-cohort percentiles */}
                  <tr className="bg-muted/30 font-bold">
                    <td className="px-3 py-2.5 text-xs">
                      {mode === 'cross_type' ? (
                        <InlinePopover
                          className="inline-flex items-baseline gap-1 cursor-help border-b border-dotted border-muted-foreground/40 hover:border-muted-foreground"
                          title="Percentile rank within each ticker's own cohort (D or ND) across your whole watchlist"
                          testId="compare-pillar-tip-percentile"
                          maxWidth={280}
                          render={() => (
                            <p className="text-left text-[11px] leading-snug text-foreground/90">
                              Dividend (D) and Non-Dividend (ND) composites use different rubrics, so raw scores aren&apos;t comparable. This row converts each ticker&apos;s composite to a PERCENTILE RANK within its own cohort across your whole watchlist — 80th means it beats 80% of its cohort.
                            </p>
                          )}
                        >
                          Percentile
                          <span className="text-[10px] text-muted-foreground font-mono">in cohort</span>
                        </InlinePopover>
                      ) : (
                        <>
                          Composite
                          <span className="text-[10px] text-muted-foreground ml-1 font-mono">/100</span>
                        </>
                      )}
                    </td>
                    {displayRows.map(r => {
                      if (mode === 'cross_type') {
                        const info = cohortPercentiles[r.ticker];
                        const isWinner = crossPctLeader && crossPctLeader.ticker === r.ticker;
                        return (
                          <td key={r.ticker} className="px-3 py-2.5 text-center" data-testid={`compare-percentile-${r.ticker}`}>
                            <div className="flex items-center justify-center gap-1">
                              {isWinner && <Trophy className="w-3.5 h-3.5 text-amber-500" />}
                              {info ? (
                                <span className="font-mono text-sm font-extrabold text-foreground">
                                  {ordinal(info.pct)}
                                  <span className={`ml-1 text-[9px] font-bold px-1 py-px rounded-sm align-middle ${info.cohort === 'ND' ? 'bg-violet-500/20 text-violet-600 dark:text-violet-300' : 'bg-fuchsia-500/20 text-fuchsia-600 dark:text-fuchsia-300'}`}>
                                    {info.cohort}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </div>
                          </td>
                        );
                      }
                      const score = r.quality?.score;
                      const isWinner = leaders.total && leaders.total.ticker === r.ticker;
                      const band = qualityBand(score);
                      const tonePill = {
                        emerald: 'text-emerald-600 dark:text-emerald-400',
                        green: 'text-green-600 dark:text-green-400',
                        lime: 'text-lime-600 dark:text-lime-400',
                        amber: 'text-amber-600 dark:text-amber-400',
                        orange: 'text-orange-600 dark:text-orange-400',
                        rose: 'text-rose-600 dark:text-rose-400',
                        muted: 'text-muted-foreground',
                      }[band.tone] || 'text-muted-foreground';
                      return (
                        <td key={r.ticker} className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {isWinner && <Trophy className="w-3.5 h-3.5 text-amber-500" />}
                            <span className={`font-mono text-base font-extrabold ${tonePill}`}>
                              {score ?? '—'}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {mode === 'cross_type' && (
                    <tr>
                      <td colSpan={displayRows.length + 1} className="px-3 py-2 text-[10px] text-muted-foreground italic" data-testid="compare-crosstype-caption">
                        D-scale and ND-scale composites use different rubrics — shown as percentile ranks within each ticker&apos;s own watchlist cohort so they&apos;re comparable.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t text-[10px] text-muted-foreground shrink-0 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3 text-amber-500" /> winner per pillar</span>
          <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3 text-emerald-500" /> higher = better</span>
          {mode === 'dividend' && (
            <span className="inline-flex items-center gap-1"><TrendingDown className="w-3 h-3 text-rose-500" /> Penalty: lower = better</span>
          )}
          <span className="flex-1" />
          {validSelected.length >= 2 && (
            <>
              <button
                type="button"
                onClick={handleCopyImage}
                disabled={copying}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border border-border hover:border-fuchsia-500/40 hover:text-fuchsia-500 transition-colors disabled:opacity-50"
                data-testid="compare-quality-copy-image"
                title="Copy the comparison table as an image"
              >
                <Camera className="w-3.5 h-3.5" />
                {copying ? 'Capturing…' : 'Copy image'}
              </button>
              <ShareToXButton
                ticker={(mode === 'cross_type' ? crossPctLeader?.ticker : leaders.total?.ticker) || rows[0]?.ticker}
                text={shareText}
                hashtags={['dividends', 'investing']}
                variant="button"
                label="Share"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
